import { useState, useEffect, useCallback } from 'react';
import { useAdmin } from '../../context/AdminContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { 
    Search, User, Building2, FileText, MessageSquare, 
    MoreVertical, Ban, CheckCircle, CreditCard, ChevronLeft, ChevronRight, Trash2 
} from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';

const AdminUsersPage = () => {
    const { api } = useAdmin();
    const [users, setUsers] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [accountType, setAccountType] = useState('all');
    const [page, setPage] = useState(0);
    const [selectedUser, setSelectedUser] = useState(null);
    const [plans, setPlans] = useState([]);
    const [subDialogOpen, setSubDialogOpen] = useState(false);
    const [deleteSubDialogOpen, setDeleteSubDialogOpen] = useState(false);
    const [deletingSub, setDeletingSub] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState('');
    const [billingCycle, setBillingCycle] = useState('monthly');
    const limit = 10;

    const fetchUsers = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ skip: page * limit, limit });
            if (accountType !== 'all') params.append('account_type', accountType);
            if (search) params.append('search', search);
            
            const response = await api.get(`/admin/users?${params}`);
            setUsers(response.data.users);
            setTotal(response.data.total);
        } catch (error) {
            toast.error('Failed to fetch users');
        } finally {
            setLoading(false);
        }
    }, [api, page, accountType, search]);

    const fetchPlans = useCallback(async () => {
        try {
            const response = await api.get('/admin/plans');
            setPlans(response.data);
        } catch (error) {
            console.error('Failed to fetch plans:', error);
        }
    }, [api]);

    useEffect(() => {
        fetchUsers();
        fetchPlans();
    }, [fetchUsers, fetchPlans]);

    const handleSearch = (e) => {
        e.preventDefault();
        setPage(0);
        fetchUsers();
    };

    const handleStatusChange = async (userId, newStatus) => {
        try {
            await api.put(`/admin/users/${userId}/status?status=${newStatus}`);
            toast.success(`User ${newStatus === 'active' ? 'activated' : 'suspended'} successfully`);
            fetchUsers();
        } catch (error) {
            toast.error('Failed to update user status');
        }
    };

    const handleSubscriptionUpdate = async () => {
        if (!selectedUser || !selectedPlan) return;
        
        try {
            await api.put(`/admin/users/${selectedUser.id}/subscription?plan_id=${selectedPlan}&billing_cycle=${billingCycle}`);
            toast.success('Subscription updated successfully');
            setSubDialogOpen(false);
            setSelectedUser(null);
            fetchUsers();
        } catch (error) {
            toast.error('Failed to update subscription');
        }
    };

    const openSubDialog = (user) => {
        setSelectedUser(user);
        setSelectedPlan(user.subscription?.plan_id || '');
        setBillingCycle(user.subscription?.billing_cycle || 'monthly');
        setSubDialogOpen(true);
    };

    const openDeleteSubDialog = (user) => {
        setSelectedUser(user);
        setDeleteSubDialogOpen(true);
    };

    const handleDeleteSubscription = async () => {
        if (!selectedUser) return;
        setDeletingSub(true);
        try {
            await api.delete(`/admin/users/${selectedUser.id}/subscription`);
            toast.success('Subscription deleted — user reset as never subscribed');
            setDeleteSubDialogOpen(false);
            setSelectedUser(null);
            fetchUsers();
        } catch (error) {
            toast.error('Failed to delete subscription');
        } finally {
            setDeletingSub(false);
        }
    };

    const totalPages = Math.ceil(total / limit);

    return (
        <div className="space-y-6" data-testid="admin-users-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-white">User Management</h1>
                    <p className="text-slate-400 mt-1">Manage platform users and their subscriptions</p>
                </div>
                <Badge variant="outline" className="border-slate-600 text-slate-300 w-fit">
                    {total} total users
                </Badge>
            </div>

            {/* Filters */}
            <Card className="border-slate-700 bg-slate-800/50">
                <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <form onSubmit={handleSearch} className="flex-1 flex gap-2">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                                <Input
                                    placeholder="Search by name or email..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    className="pl-9 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
                                    data-testid="user-search-input"
                                />
                            </div>
                            <Button type="submit" variant="secondary" className="bg-slate-700 hover:bg-slate-600">
                                Search
                            </Button>
                        </form>
                        <Select value={accountType} onValueChange={setAccountType}>
                            <SelectTrigger className="w-[180px] bg-slate-700/50 border-slate-600 text-white" data-testid="account-type-filter">
                                <SelectValue placeholder="Account Type" />
                            </SelectTrigger>
                            <SelectContent className="bg-slate-800 border-slate-700">
                                <SelectItem value="all" className="text-white">All Types</SelectItem>
                                <SelectItem value="individual" className="text-white">Individual</SelectItem>
                                <SelectItem value="laboratory" className="text-white">Laboratory</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            {/* Users Table */}
            <Card className="border-slate-700 bg-slate-800/50">
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <table className="w-full" data-testid="users-table">
                            <thead>
                                <tr className="border-b border-slate-700">
                                    <th className="text-left p-4 text-slate-400 font-medium">User</th>
                                    <th className="text-left p-4 text-slate-400 font-medium">Type</th>
                                    <th className="text-left p-4 text-slate-400 font-medium">Subscription</th>
                                    <th className="text-left p-4 text-slate-400 font-medium">Usage</th>
                                    <th className="text-left p-4 text-slate-400 font-medium">Status</th>
                                    <th className="text-right p-4 text-slate-400 font-medium">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={6} className="p-8 text-center text-slate-400">
                                            Loading users...
                                        </td>
                                    </tr>
                                ) : users.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="p-8 text-center text-slate-400">
                                            No users found
                                        </td>
                                    </tr>
                                ) : (
                                    users.map((user) => (
                                        <tr key={user.id} className="border-b border-slate-700/50 hover:bg-slate-700/30">
                                            <td className="p-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
                                                        {user.account_type === 'laboratory' ? (
                                                            <Building2 className="w-5 h-5 text-teal-400" />
                                                        ) : (
                                                            <User className="w-5 h-5 text-slate-400" />
                                                        )}
                                                    </div>
                                                    <div>
                                                        <p className="text-white font-medium">{user.name}</p>
                                                        <p className="text-sm text-slate-400">{user.email}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="p-4">
                                                <Badge 
                                                    variant="outline" 
                                                    className={user.account_type === 'laboratory' 
                                                        ? 'border-teal-500/50 text-teal-400' 
                                                        : 'border-slate-600 text-slate-300'}
                                                >
                                                    {user.account_type}
                                                </Badge>
                                            </td>
                                            <td className="p-4">
                                                {user.subscription ? (
                                                    <Badge className="bg-teal-500/20 text-teal-400 border-0">
                                                        {user.subscription.plan_name}
                                                    </Badge>
                                                ) : (
                                                    <span className="text-slate-500">No plan</span>
                                                )}
                                            </td>
                                            <td className="p-4">
                                                <div className="flex items-center gap-4 text-sm text-slate-400">
                                                    <span className="flex items-center gap-1">
                                                        <FileText className="w-4 h-4" />
                                                        {user.usage?.reports || 0}
                                                    </span>
                                                    <span className="flex items-center gap-1">
                                                        <MessageSquare className="w-4 h-4" />
                                                        {user.usage?.ai_chats || 0}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="p-4">
                                                <Badge 
                                                    className={user.status === 'suspended' 
                                                        ? 'bg-red-500/20 text-red-400 border-0' 
                                                        : 'bg-green-500/20 text-green-400 border-0'}
                                                >
                                                    {user.status || 'active'}
                                                </Badge>
                                            </td>
                                            <td className="p-4 text-right">
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-400 hover:text-white" data-testid={`user-actions-${user.id}`}>
                                                            <MoreVertical className="w-4 h-4" />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end" className="bg-slate-800 border-slate-700">
                                                        <DropdownMenuItem 
                                                            className="text-white hover:bg-slate-700 cursor-pointer"
                                                            onClick={() => openSubDialog(user)}
                                                            data-testid={`manage-subscription-btn-${user.id}`}
                                                        >
                                                            <CreditCard className="w-4 h-4 mr-2" />
                                                            Manage Subscription
                                                        </DropdownMenuItem>
                                                        {user.subscription && (
                                                            <DropdownMenuItem 
                                                                className="text-red-400 hover:bg-slate-700 cursor-pointer"
                                                                onClick={() => openDeleteSubDialog(user)}
                                                                data-testid={`delete-subscription-btn-${user.id}`}
                                                            >
                                                                <Trash2 className="w-4 h-4 mr-2" />
                                                                Delete Subscription
                                                            </DropdownMenuItem>
                                                        )}
                                                        {user.status === 'suspended' ? (
                                                            <DropdownMenuItem 
                                                                className="text-green-400 hover:bg-slate-700 cursor-pointer"
                                                                onClick={() => handleStatusChange(user.id, 'active')}
                                                            >
                                                                <CheckCircle className="w-4 h-4 mr-2" />
                                                                Activate User
                                                            </DropdownMenuItem>
                                                        ) : (
                                                            <DropdownMenuItem 
                                                                className="text-red-400 hover:bg-slate-700 cursor-pointer"
                                                                onClick={() => handleStatusChange(user.id, 'suspended')}
                                                            >
                                                                <Ban className="w-4 h-4 mr-2" />
                                                                Suspend User
                                                            </DropdownMenuItem>
                                                        )}
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-between p-4 border-t border-slate-700">
                            <p className="text-sm text-slate-400">
                                Showing {page * limit + 1} to {Math.min((page + 1) * limit, total)} of {total}
                            </p>
                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage(p => Math.max(0, p - 1))}
                                    disabled={page === 0}
                                    className="border-slate-600 text-slate-300 hover:bg-slate-700"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage(p => p + 1)}
                                    disabled={page >= totalPages - 1}
                                    className="border-slate-600 text-slate-300 hover:bg-slate-700"
                                >
                                    <ChevronRight className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Subscription Dialog */}
            <Dialog open={subDialogOpen} onOpenChange={setSubDialogOpen}>
                <DialogContent className="bg-slate-800 border-slate-700 text-white">
                    <DialogHeader>
                        <DialogTitle>Manage Subscription</DialogTitle>
                        <DialogDescription className="text-slate-400">
                            Update subscription for {selectedUser?.name}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <label className="text-sm text-slate-300">Subscription Plan</label>
                            <Select value={selectedPlan} onValueChange={setSelectedPlan}>
                                <SelectTrigger className="bg-slate-700/50 border-slate-600 text-white" data-testid="plan-select">
                                    <SelectValue placeholder="Select a plan" />
                                </SelectTrigger>
                                <SelectContent className="bg-slate-800 border-slate-700">
                                    {plans.map((plan) => (
                                        <SelectItem key={plan.id} value={plan.id} className="text-white">
                                            {plan.name} - ${plan.price_monthly}/mo
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm text-slate-300">Billing Cycle</label>
                            <Select value={billingCycle} onValueChange={setBillingCycle}>
                                <SelectTrigger className="bg-slate-700/50 border-slate-600 text-white">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent className="bg-slate-800 border-slate-700">
                                    <SelectItem value="monthly" className="text-white">Monthly</SelectItem>
                                    <SelectItem value="yearly" className="text-white">Yearly</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSubDialogOpen(false)} className="border-slate-600 text-slate-300">
                            Cancel
                        </Button>
                        <Button onClick={handleSubscriptionUpdate} className="bg-teal-500 hover:bg-teal-600">
                            Update Subscription
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Subscription Dialog */}
            <Dialog open={deleteSubDialogOpen} onOpenChange={setDeleteSubDialogOpen}>
                <DialogContent className="bg-slate-800 border-slate-700 text-white" data-testid="delete-subscription-dialog">
                    <DialogHeader>
                        <DialogTitle className="text-red-400">Delete Subscription</DialogTitle>
                        <DialogDescription className="text-slate-400">
                            This will remove the subscription of <span className="text-white font-medium">{selectedUser?.name}</span> ({selectedUser?.subscription?.plan_name}) and reset their report credits and free-trial status. The user will be as if they never subscribed. This cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteSubDialogOpen(false)} className="border-slate-600 text-slate-300" data-testid="cancel-delete-subscription-btn">
                            Cancel
                        </Button>
                        <Button onClick={handleDeleteSubscription} disabled={deletingSub} className="bg-red-500 hover:bg-red-600 text-white" data-testid="confirm-delete-subscription-btn">
                            {deletingSub ? 'Deleting...' : 'Delete Subscription'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminUsersPage;
